import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from ".";
import { IAddItemBase, ICartItemData, IDirectPurchaseItemData } from "@interfaces";
import { cartApi } from "@api";
import { LoadingStage, InitStages } from "src/Interfaces/shared";
import moment from "moment";
import { transformCartItemsToUpdateCartItemsData } from "src/Module/Checkout/checkoutHelpers";
import { sessionHelper } from "@utilities";
interface BookNowState {
    cartItems: ICartItemData[];
    directPurchaseItemData: IDirectPurchaseItemData | null;
    cartItemsLoadingStage: LoadingStage;
}

const initialState: BookNowState = {
    cartItems: [],
    directPurchaseItemData: null,
    cartItemsLoadingStage: LoadingStage.NOT_INITIALIZED,
};

// Async thunk for fetching Book Now items from cart API
export const fetchBookNowItems = createAsyncThunk<
    ICartItemData[],
    string | undefined,
    { rejectValue: string }
>("bookNow/fetchBookNowItems", async (sessionId, { rejectWithValue }) => {
    try {
        // Don't fetch cart items if user has temp JWT and no sessionId (during signup/verification flow)
        const tempJwt = sessionStorage.getItem("tempJwt");
        if (tempJwt && !sessionId) {
            return [];
        }

        const fetchCart = async (): Promise<ICartItemData[]> => {
            const res = await cartApi.getCart(sessionId);
            return res.data.data;
        };

        const items = await fetchCart();
        return items;
    } catch (err: any) {
        console.error("Failed to fetch book now items:", err);
        return rejectWithValue("Unable to fetch book now data.");
    }
});

// Async thunk for adding to Book Now
// Only one item can be in Book Now, so we clear the cart first, then add the new item
// Resets book now session ID every time Book Now is tapped
export const addToBookNow = createAsyncThunk<
    ICartItemData[],
    { item: IAddItemBase; sessionId?: string }
>("bookNow/addToBookNow", async ({ item }, { rejectWithValue }) => {
    try {
        // Reset book now session ID to start fresh
        // This ensures each "Book Now" action uses a new session
        sessionHelper.clearBookNowSession();

        // Generate a new session ID for this Book Now action
        // Always generate a new one, ignoring any passed sessionId to ensure fresh start
        const newSessionId = sessionHelper.getBookNowSessionId();

        // First, get current cart items to clear them
        const currentCartResponse = await cartApi.getCart(newSessionId);
        const currentItems = currentCartResponse.data.data as ICartItemData[];

        // Clear all existing items (Book Now only allows one item)
        if (currentItems.length > 0) {
            // Remove all items from cart
            await Promise.all(
                currentItems.map((cartItem) =>
                    cartApi.removeCartItem(
                        {
                            id: cartItem.id.toString(),
                            type: "item",
                        },
                        newSessionId,
                    ),
                ),
            );
        }

        // Add the new item to cart
        await cartApi.addCart(item, newSessionId);

        // Fetch updated cart items
        const response = await cartApi.getCart(newSessionId);
        const items = response.data.data as ICartItemData[];

        // Assign id from API or fallback to index
        const itemsWithId = items.map((item, idx) => ({
            ...item,
            id: item.id ?? idx,
        }));
        return itemsWithId;
    } catch (error: any) {
        console.error("Failed to add item in book now", error.response?.data || error);
        return rejectWithValue(
            error.response?.data || error.message || "Failed to add item in book now",
        );
    }
});

const bookNowSlice = createSlice({
    name: "bookNow",
    initialState,
    reducers: {
        setBookNowItemData(state, action: PayloadAction<ICartItemData[]>) {
            state.cartItems = action.payload;

            const updates = transformCartItemsToUpdateCartItemsData(action.payload);
            if (state.cartItems.length === 0 || updates.length === 0) {
                state.directPurchaseItemData = null;
                return;
            }

            const productPricesDetails = state.cartItems.map((item, index) => {
                const update = updates[index];
                return {
                    productPricesDetailsId: String(item.product_price_details_id),
                    quantityDetails: [
                        {
                            quantity: update.quantity,
                            bookingDate: moment(item.booking_date).format("DD-MMM-YYYY"),
                            bookingData: update.bookingData,
                        } as any,
                    ],
                };
            });
            const firstItem = state.cartItems[0];
            state.directPurchaseItemData = {
                tdmsProductId: firstItem.tdms_product_id.toString(),
                productPricesDetails,
                startDate: moment(firstItem.start_date).format("DD-MMM-YYYY"),
                days: firstItem.days,
                selectedAvailableIndices: state.cartItems.map((item) => item.selected_index),
            };
        },

        clearBookNowItem(state) {
            state.cartItems = [];
            state.directPurchaseItemData = null;
            state.cartItemsLoadingStage = LoadingStage.NOT_INITIALIZED;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBookNowItems.pending, (state) => {
                state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                    ? LoadingStage.IS_INITIALIZING
                    : LoadingStage.IS_LOADING;
            })
            .addCase(fetchBookNowItems.fulfilled, (state, action) => {
                state.cartItems = action.payload;
                // Update directPurchaseItemData when items are fetched
                if (action.payload.length > 0) {
                    const updates = transformCartItemsToUpdateCartItemsData(action.payload);
                    if (updates.length > 0) {
                        const productPricesDetails = action.payload.map((item, index) => {
                            const update = updates[index];
                            return {
                                productPricesDetailsId: String(item.product_price_details_id),
                                quantityDetails: [
                                    {
                                        quantity: update.quantity,
                                        bookingDate: moment(item.booking_date).format(
                                            "DD-MMM-YYYY",
                                        ),
                                        bookingData: update.bookingData,
                                    } as any,
                                ],
                            };
                        });
                        const firstItem = action.payload[0];
                        state.directPurchaseItemData = {
                            tdmsProductId: firstItem.tdms_product_id.toString(),
                            productPricesDetails,
                            startDate: moment(firstItem.start_date).format("DD-MMM-YYYY"),
                            days: firstItem.days,
                            selectedAvailableIndices: action.payload.map(
                                (item) => item.selected_index,
                            ),
                        };
                    }
                } else {
                    state.directPurchaseItemData = null;
                }
                state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                    ? LoadingStage.INITIALIZE_COMPLETE
                    : LoadingStage.LOADING_COMPLETE;
            })
            .addCase(fetchBookNowItems.rejected, (state) => {
                state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                    ? LoadingStage.INITIALIZE_FAILURE
                    : LoadingStage.LOADING_FAILURE;
            })
            .addCase(addToBookNow.pending, (state) => {
                state.cartItemsLoadingStage = LoadingStage.IS_INITIALIZING;
            })
            .addCase(addToBookNow.fulfilled, (state, action) => {
                state.cartItems = action.payload;
                // Update directPurchaseItemData when items are added
                if (action.payload.length > 0) {
                    const updates = transformCartItemsToUpdateCartItemsData(action.payload);
                    if (updates.length > 0) {
                        const productPricesDetails = action.payload.map((item, index) => {
                            const update = updates[index];
                            return {
                                productPricesDetailsId: String(item.product_price_details_id),
                                quantityDetails: [
                                    {
                                        quantity: update.quantity,
                                        bookingDate: moment(item.booking_date).format(
                                            "DD-MMM-YYYY",
                                        ),
                                        bookingData: update.bookingData,
                                    } as any,
                                ],
                            };
                        });
                        const firstItem = action.payload[0];
                        state.directPurchaseItemData = {
                            tdmsProductId: firstItem.tdms_product_id.toString(),
                            productPricesDetails,
                            startDate: moment(firstItem.start_date).format("DD-MMM-YYYY"),
                            days: firstItem.days,
                            selectedAvailableIndices: action.payload.map(
                                (item) => item.selected_index,
                            ),
                        };
                    }
                } else {
                    state.directPurchaseItemData = null;
                }
                state.cartItemsLoadingStage = LoadingStage.INITIALIZE_COMPLETE;
            })
            .addCase(addToBookNow.rejected, (state) => {
                state.cartItemsLoadingStage = LoadingStage.NOT_INITIALIZED;
            });
    },
});

export const { setBookNowItemData, clearBookNowItem } = bookNowSlice.actions;
export const selectBookNow = (state: RootState) => state.bookNow;
export default bookNowSlice.reducer;
